import { EmployeeRole, MenuAccess, User, TOKEN_KEYS, REMEMBER_ME_DURATION } from '@/types/auth';

// Token management utilities
export const tokenStorage = {
  // Get storage type based on remember me
  getStorage: (rememberMe: boolean = false) => {
    return rememberMe ? localStorage : sessionStorage;
  },

  // Save tokens
  saveTokens: (accessToken: string, refreshToken: string, rememberMe: boolean = false) => {
    const storage = tokenStorage.getStorage(rememberMe);
    storage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
    storage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
    storage.setItem(TOKEN_KEYS.REMEMBER_ME, rememberMe.toString());
  },

  // Get tokens
  getTokens: () => {
    const rememberMe = localStorage.getItem(TOKEN_KEYS.REMEMBER_ME) === 'true';
    const storage = tokenStorage.getStorage(rememberMe);
    
    return {
      accessToken: storage.getItem(TOKEN_KEYS.ACCESS_TOKEN),
      refreshToken: storage.getItem(TOKEN_KEYS.REFRESH_TOKEN),
      rememberMe
    };
  },

  // Clear tokens
  clearTokens: () => {
    localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.REMEMBER_ME);
    localStorage.removeItem(TOKEN_KEYS.USER_DATA);
    
    sessionStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
    sessionStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
    sessionStorage.removeItem(TOKEN_KEYS.REMEMBER_ME);
    sessionStorage.removeItem(TOKEN_KEYS.USER_DATA);
  },

  // Save user data
  saveUser: (user: User, rememberMe: boolean = false) => {
    const storage = tokenStorage.getStorage(rememberMe);
    storage.setItem(TOKEN_KEYS.USER_DATA, JSON.stringify(user));
  },

  // Get user data
  getUser: (): User | null => {
    const rememberMe = localStorage.getItem(TOKEN_KEYS.REMEMBER_ME) === 'true';
    const storage = tokenStorage.getStorage(rememberMe);
    const userData = storage.getItem(TOKEN_KEYS.USER_DATA);
    
    if (userData) {
      try {
        return JSON.parse(userData);
      } catch {
        return null;
      }
    }
    return null;
  },

  // Check if token is expired
  isTokenExpired: (token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  },

  // Check if remember me is still valid
  isRememberMeValid: (): boolean => {
    const rememberMe = localStorage.getItem(TOKEN_KEYS.REMEMBER_ME) === 'true';
    if (!rememberMe) return false;

    const timestamp = localStorage.getItem('tailortrack_remember_me_timestamp');
    if (!timestamp) return false;

    const savedTime = parseInt(timestamp);
    return Date.now() - savedTime < REMEMBER_ME_DURATION;
  },

  // Set remember me timestamp
  setRememberMeTimestamp: () => {
    localStorage.setItem('tailortrack_remember_me_timestamp', Date.now().toString());
  }
};

// Role-based permissions
export const getMenuAccess = (role: EmployeeRole): MenuAccess => {
  const baseAccess: MenuAccess = {
    dashboard: false,
    employees: false,
    customers: false,
    measurements: false,
    orders: false,
    workflows: false,
    reports: false,
    settings: false
  };

  switch (role) {
    case 'SUPER_ADMIN':
      return {
        ...baseAccess,
        dashboard: true,
        employees: true,
        customers: true,
        measurements: true,
        orders: true,
        workflows: true,
        reports: true,
        settings: true
      };

    case 'ADMINISTRATOR':
      return {
        ...baseAccess,
        dashboard: true,
        employees: true,
        customers: true,
        measurements: true,
        orders: true,
        workflows: true,
        reports: true,
        settings: true
      };

    case 'PRODUCTION_CONTROLLER':
      return {
        ...baseAccess,
        workflows: true,
        reports: true
      };

    case 'SALESMAN':
      return {
        ...baseAccess,
        dashboard: true,
        customers: true,
        measurements: true,
        orders: true
      };

    case 'RECEPTION':
      return {
        ...baseAccess,
        dashboard: true,
        customers: true,
        measurements: true,
        orders: true,
        workflows: true
      };

    default:
      return baseAccess;
  }
};

// Check if user can login
export const canLogin = (role: EmployeeRole): boolean => {
  return ['SUPER_ADMIN', 'ADMINISTRATOR', 'PRODUCTION_CONTROLLER', 'SALESMAN', 'RECEPTION'].includes(role);
};

// Avatar utilities
export const getAvatarDisplay = (user: User) => {
  if (user.profilePicture) {
    return {
      type: 'image' as const,
      src: user.profilePicture,
      alt: user.fullName
    };
  }

  return {
    type: 'initials' as const,
    initials: getInitials(user.fullName),
    name: user.fullName
  };
};

// Get initials from full name
export const getInitials = (fullName: string): string => {
  return fullName
    .split(' ')
    .map(name => name.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Format role for display
export const formatRole = (role: EmployeeRole): string => {
  return role.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
};

// API utilities
export const getAuthHeaders = (token: string) => ({
  'Authorization': `Bearer ${token}`,
  'Content-Type': 'application/json'
});

// Error message formatting
export const formatAuthError = (error: any): string => {
  if (typeof error === 'string') return error;
  
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  
  if (error?.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
};
